// src/components/castcrew/CastCrew.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const CastCrew = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const [activeTab, setActiveTab] = useState('cast');
  const [castData, setCastData] = useState([]);
  const [crewData, setCrewData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // const { selectedResult } = useAppContext(); // Removed context usage

  // Function to fetch cast data (wrapped in useCallback)
  const fetchCastData = useCallback(async (tmsId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getCast(tmsId);
      setCastData(data || []);
    } catch (err) {
      console.error('Failed to fetch cast data:', err);
      setError(t('programCastCrew.errorCast'));
      setCastData([]);
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array

  // Function to fetch crew data (wrapped in useCallback)
  const fetchCrewData = useCallback(async (tmsId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getCrew(tmsId);
      setCrewData(data || []);
    } catch (err) {
      console.error('Failed to fetch crew data:', err);
      setError(t('programCastCrew.errorCrew'));
      setCrewData([]);
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array

  // Effect to fetch data when programId or activeTab changes
  useEffect(() => {
    if (programId) {
      if (activeTab === 'cast') {
        fetchCastData(programId);
      } else {
        fetchCrewData(programId);
      }
    } else {
      // Clear data if programId is missing
      setCastData([]);
      setCrewData([]);
    }
  }, [programId, activeTab, fetchCastData, fetchCrewData]); // Added dependencies

  // Handle tab change (simplified, useEffect handles fetching)
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const navigate = useNavigate();

  // Render loading state
  if (isLoading) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">{t('programCastCrew.loading', { tab: activeTab === 'cast' ? t('programCastCrew.tabs.cast') : t('programCastCrew.tabs.crew') })}</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-4">{error}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => activeTab === 'cast' ? fetchCastData(programId) : fetchCrewData(programId)} // Use programId
          >
            {t('programTitles.tryAgain')}
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="w-full p-2 h-full overflow-auto">
      <div className="flex mb-4">
        <button 
          className={`px-4 py-2 text-xs ${activeTab === 'cast' ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => handleTabChange('cast')}
        >
          {t('programCastCrew.tabs.cast')}
        </button>
        <button 
          className={`px-4 py-2 text-xs ${activeTab === 'crew' ? 'bg-blue-900 text-white' : 'bg-gray-100 text-gray-600'}`}
          onClick={() => handleTabChange('crew')}
        >
          {t('programCastCrew.tabs.crew')}
        </button>
      </div>

      {/* Header section */}
      <div className="mb-3">
        <h2 className="text-lg font-semibold mb-1">{activeTab === 'cast' ? t('programCastCrew.tabs.cast') : t('programCastCrew.tabs.crew')}</h2>
        {/* Removed dependency on selectedResult.title */}
        <p className="text-sm text-gray-600 mb-2">
          {activeTab === 'cast'
            ? t('programCastCrew.descCast')
            : t('programCastCrew.descCrew')}
        </p>
      </div>

      {/* Cast/Crew Gallery View */}
      {activeTab === 'cast' && (
        <div className="w-full">
          {castData.length === 0 ? (
            <div className="w-full h-full p-4 flex justify-center items-center">
              <div className="text-gray-500 text-center">
                <p>{t('programCastCrew.noCast')}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {castData.map((person) => (
                <div
                  key={person.personId}
                  className="flex flex-col items-center text-center border border-gray-100 bg-gray-50 rounded-md overflow-hidden pb-2 hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <div className="w-full aspect-square overflow-hidden flex items-center justify-center p-2">
                    {person.image_url ? (
                      <img
                        src={person.image_url}
                        alt={person.actor_name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://panorama.telus.encoreinteractive.ca/offline.png';
                        }}
                        onClick={
                          person.personId
                            ? () =>
                                navigate(`/celebrities/${person.personId}`, {
                                  state: { image_url: person.image_url },
                                })
                            : undefined
                        }
                        style={person.personId ? {} : {}}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400"
                        onClick={
                          person.personId
                            ? () => navigate(`/celebrities/${person.personId}`)
                            : undefined
                        }
                        style={person.personId ? {} : {}}
                      >
                        {t('programImages.noImage')}
                      </div>
                    )}
                  </div>
                  <div className="p-1 w-full">
                    {person.personId ? (
                      <button
                        type="button"
                        className="font-medium text-sm text-blue-700 underline hover:text-blue-900 cursor-pointer bg-transparent border-0 p-0 m-0"
                        style={{ textAlign: 'left' }}
                        onClick={() =>
                          navigate(`/celebrities/${person.personId}`, {
                            state: { image_url: person.image_url },
                          })
                        }
                        title="View Celebrity Page"
                      >
                        {person.actor_name}
                      </button>
                    ) : (
                      <h3 className="font-medium text-sm">{person.actor_name}</h3>
                    )}
                    <p className="text-xs text-gray-600">{person.cast_name}</p>
                    <p className="text-xs text-gray-500">{person.role}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Crew Gallery View - Using same layout as Cast */}
      {activeTab === 'crew' && (
        <div className="w-full">
          {crewData.length === 0 ? (
            <div className="w-full h-full p-4 flex justify-center items-center">
              <div className="text-gray-500 text-center">
                <p>{t('programCastCrew.noCrew')}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {crewData.map((person) => (
                <div
                  key={person.personId}
                  className="flex flex-col items-center text-center border border-gray-100 bg-gray-50 rounded-md overflow-hidden pb-2 hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <div className="w-full aspect-square overflow-hidden flex items-center justify-center p-2">
                    {person.image_url ? (
                      <img
                        src={person.image_url}
                        alt={person.crew_name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://panorama.telus.encoreinteractive.ca/offline.png';
                        }}
                        onClick={
                          person.personId
                            ? () => navigate(`/celebrities/${person.personId}`)
                            : undefined
                        }
                        style={person.personId ? {} : {}}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400"
                        onClick={
                          person.personId
                            ? () => navigate(`/celebrities/${person.personId}`)
                            : undefined
                        }
                        style={person.personId ? {} : {}}
                      >
                        {t('programImages.noImage')}
                      </div>
                    )}
                  </div>
                  <div className="p-1 w-full">
                    {person.personId ? (
                      <button
                        type="button"
                        className="font-medium text-sm text-blue-700 underline hover:text-blue-900 cursor-pointer bg-transparent border-0 p-0 m-0"
                        style={{ textAlign: 'left' }}
                        onClick={() => navigate(`/celebrities/${person.personId}`)}
                        title="View Celebrity Page"
                      >
                        {person.crew_name}
                      </button>
                    ) : (
                      <h3 className="font-medium text-sm">{person.crew_name}</h3>
                    )}
                    <p className="text-xs text-gray-600">{person.job || person.department}</p>
                    <p className="text-xs text-gray-500">{person.role}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-between text-xs text-gray-500">
        <div>
          {activeTab === 'cast' 
            ? t('programCastCrew.showingCast', { count: castData.length })
            : t('programCastCrew.showingCrew', { count: crewData.length })
          }
        </div>
        <div>
          <button
            className={`px-3 py-1 border border-gray-300 rounded-sm ${activeTab === 'cast' ? (castData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100') : (crewData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100')}`}
            onClick={() => exportToCsv(activeTab === 'cast' ? castData : crewData, `${programId}_${activeTab}.csv`)}
            disabled={activeTab === 'cast' ? castData.length === 0 : crewData.length === 0}
          >
            {activeTab === 'cast' ? t('programCastCrew.exportCast') : t('programCastCrew.exportCrew')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CastCrew;
